import React, { createContext, useContext, useState, useEffect } from 'react';

// Available languages with their flags
export const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
    { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية', rtl: true },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳', nativeName: 'हिन्दी' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵', nativeName: '日本語' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺', nativeName: 'Русский' },
    { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
];

// Comprehensive translations
const translations = {
    en: {
        // Navigation
        aiInsights: 'AI Insights',
        rateMatrix: 'Rate Matrix',
        controlTower: 'Control Tower',
        notificationCenter: 'Notification Center',
        rateQuote: 'Rate Quote',
        rfq: 'RFQ',
        quoteFollowup: 'Quote Followup',
        purchaseOrder: 'Purchase Order',
        salesOrder: 'Sales Order',
        loadPlan: 'Load Plan',
        booking: 'Booking',
        aiAutomation: 'AI Automation',
        aiAgentSetup: 'AI Agent Setup',
        api: 'API',
        visionBoard: 'Vision Board',
        addressBook: 'Address Book',

        // Accounting Module
        accounting: 'Accounting',
        chartOfAccounts: 'Chart of Accounts',
        manageGLAccounts: 'Manage your general ledger accounts',
        chargeCodes: 'Charge Codes',
        manageChargeCodes: 'Manage charge codes for billing',
        customerInvoices: 'Customer Invoices',
        manageAR: 'Manage accounts receivable',
        vendorBills: 'Vendor Bills',
        manageAP: 'Manage accounts payable',
        journalEntries: 'Journal Entries',
        manageJournals: 'Manage journal entries',
        payments: 'Payments',
        managePayments: 'Manage payments and receipts',
        reports: 'Reports',
        financialReports: 'Financial Reports',
        analyticsReporting: 'Analytics and reporting dashboard',

        // Dashboard
        aiDrivenSummary: 'Shipez AI-Driven Summary',
        logisticsOverview: 'Logistics Overview',
        aiSummary: 'AI Summary',
        loadingAISummary: 'Loading AI summary...',
        activeShipments: 'Active Shipments',
        pendingQuotes: 'Pending Quotes',
        inTransitContainer: 'In Transit Container',
        revenue: 'Revenue',
        fromLastMonth: 'from last month',
        from: 'From',
        to: 'To',
        create: 'Create',

        // Chart of Accounts Page
        addAccount: 'Add Account',
        editAccount: 'Edit Account',
        accountCode: 'Account Code',
        accountName: 'Account Name',
        accountType: 'Account Type',
        subType: 'Sub-Type',
        parentAccount: 'Parent Account',
        description: 'Description',
        openingBalance: 'Opening Balance',
        currentBalance: 'Current Balance',
        isActive: 'Is Active',
        asset: 'Asset',
        liability: 'Liability',
        equity: 'Equity',
        income: 'Income',
        expense: 'Expense',
        cogs: 'COGS',
        banks: 'Banks',
        creditCards: 'Credit Cards',
        allTypes: 'All Types',
        allSubTypes: 'All Sub-Types',
        searchAccounts: 'Search accounts...',
        noAccountsFound: 'No accounts found. Click "Add Account" to create one.',

        // Charge Codes Page
        addChargeCode: 'Add Charge Code',
        editChargeCode: 'Edit Charge Code',
        chargeCode: 'Charge Code',
        chargeName: 'Charge Name',
        chargeCategory: 'Category',
        defaultRate: 'Default Rate',
        taxable: 'Taxable',
        glAccount: 'GL Account',
        searchChargeCodes: 'Search charge codes...',
        noChargeCodesFound: 'No charge codes found. Click "Add Charge Code" to create one.',

        // Customer Invoices Page
        newInvoice: 'New Invoice',
        editInvoice: 'Edit Invoice',
        invoiceNumber: 'Invoice #',
        invoiceDate: 'Invoice Date',
        dueDate: 'Due Date',
        customer: 'Customer',
        invoiceType: 'Type',
        amount: 'Amount',
        balance: 'Balance',
        status: 'Status',
        allInvoices: 'All Invoices',
        draft: 'Draft',
        issued: 'Issued',
        partial: 'Partial',
        paid: 'Paid',
        void: 'Void',
        outstanding: 'Outstanding',
        totalOutstanding: 'Total Outstanding',
        searchInvoices: 'Search invoices...',
        allStatuses: 'All Statuses',
        noInvoicesFound: 'No invoices found. Click "New Invoice" to create one.',

        // Vendor Bills Page
        newBill: 'New Bill',
        editBill: 'Edit Bill',
        billNumber: 'Bill #',
        billDate: 'Bill Date',
        vendor: 'Vendor',
        allBills: 'All Bills',
        pending: 'Pending',
        approved: 'Approved',
        searchBills: 'Search bills...',
        noBillsFound: 'No bills found. Click "New Bill" to create one.',

        // Journal Entries Page
        newJournal: 'New Journal Entry',
        editJournal: 'Edit Journal Entry',
        journalNumber: 'Journal #',
        journalDate: 'Date',
        reference: 'Reference',
        memo: 'Memo',
        debit: 'Debit',
        credit: 'Credit',
        totalDebit: 'Total Debit',
        totalCredit: 'Total Credit',
        balanced: 'Balanced',
        unbalanced: 'Unbalanced',
        posted: 'Posted',
        searchJournals: 'Search journal entries...',
        noJournalsFound: 'No journal entries found. Click "New Journal Entry" to create one.',
        addLine: 'Add Line',
        removeLine: 'Remove Line',

        // Payments Page
        newPayment: 'New Payment',
        recordPayment: 'Record Payment',
        paymentNumber: 'Payment #',
        paymentDate: 'Payment Date',
        paymentMethod: 'Payment Method',
        paymentAmount: 'Amount',
        appliedTo: 'Applied To',
        cash: 'Cash',
        check: 'Check',
        bankTransfer: 'Bank Transfer',
        creditCard: 'Credit Card',
        receipt: 'Receipt',
        payment: 'Payment',
        searchPayments: 'Search payments...',
        noPaymentsFound: 'No payments found. Click "New Payment" to record one.',

        // Reports Page
        summary: 'Summary',
        arAging: 'AR Aging',
        apAging: 'AP Aging',
        trialBalance: 'Trial Balance',
        totalRevenue: 'Total Revenue',
        totalExpenses: 'Total Expenses',
        netProfit: 'Net Profit',
        pendingAR: 'Pending AR',
        pendingAP: 'Pending AP',
        totalInvoices: 'Total Invoices',
        paidInvoices: 'Paid Invoices',
        totalBills: 'Total Bills',
        paidBills: 'Paid Bills',
        export: 'Export',
        refresh: 'Refresh',
        current: 'Current',
        daysOverdue: '{days} Days',
        overdue: 'Overdue',

        // Booking Form
        customerBookingInfo: 'Customer & Booking Information',
        editingBooking: 'Editing Booking',
        newBookingTitle: 'New Booking',
        customerShipper: 'Customer / Shipper',
        contactName: 'Contact Name',
        telephone: 'Telephone',
        email: 'Email',
        salesPerson: 'Sales Person',
        blMode: 'BL Mode',
        officeType: 'Office Type',
        fileNumber: 'File Number',
        customerRef: 'Customer Ref. #',
        carrierBookingNumber: 'Carrier Booking Number',
        carrierBLNumber: 'Carrier BL Number',
        bookingType: 'Booking Type',
        fileManager: 'File Manager',
        coLoaderRefNo: 'Co-loader Ref No',
        incoTerm: 'Inco Term',
        rateQuoteNumber: 'RateQuote Number',
        shippingType: 'Shipping Type',
        fcl: 'FCL',
        lcl: 'LCL',
        roRo: 'RO-RO',
        fullContainer: 'Full Container',
        lessContainer: 'Less Container',
        rollOnRollOff: 'Roll-on/Roll-off',

        // Shipment Details
        shipmentDetails: 'Shipment Details',
        consignee: 'Consignee',
        notifyParty: 'Notify Party',
        carrier: 'Carrier',
        vessel: 'Vessel',
        voyage: 'Voyage',
        portOfLoading: 'Port of Loading',
        portOfDischarge: 'Port of Discharge',
        placeOfDelivery: 'Place of Delivery',
        etd: 'ETD',
        eta: 'ETA',

        // Container Details
        containerDetails: 'Container Details',
        containerNumber: 'Container Number',
        containerType: 'Container Type',
        sealNumber: 'Seal Number',
        weight: 'Weight',
        volume: 'Volume',
        pieces: 'Pieces',
        packageType: 'Package Type',

        // Cargo Details
        cargoDetails: 'Cargo Details',
        cargoDescription: 'Cargo Description',
        hsCode: 'HS Code',
        grossWeight: 'Gross Weight',
        netWeight: 'Net Weight',
        cbm: 'CBM',

        // Print Templates
        downloadPDF: 'Download PDF',
        print: 'Print',
        loadingQuotation: 'Loading quotation...',
        customer: 'Customer',
        rqNumber: 'RQ NUMBER',
        quoteDate: 'QUOTE DATE',
        effectiveDate: 'EFFECTIVE DATE',
        expiryDate: 'EXPIRY DATE',
        quotedBy: 'QUOTED BY',
        salesPerson: 'SALES PERSON',
        routingInstruction: 'Routing Instruction',
        chargesBreakdown: 'Charges Breakdown',
        remarks: 'Remarks',
        subtotal: 'Subtotal',
        grandTotal: 'Grand Total',
        termsAndConditions: 'Terms and Conditions',
        preparedBy: 'Prepared By',
        authorizedSignature: 'Authorized Signature',
        thankYouForBusiness: 'Thank you for your business!',

        // AR/AP Section
        arApInformation: 'A/R & A/P Information',
        manageRevenueAndCost: 'Manage revenue and cost charges',
        accountsReceivable: 'Accounts Receivable',
        accountsPayable: 'Accounts Payable',
        revenue: 'Revenue',
        costs: 'Costs',
        totalAR: 'Total AR',
        totalAP: 'Total AP',
        profit: 'Profit',
        margin: 'Margin',
        addARCharge: 'Add AR Charge',
        addAPCharge: 'Add AP Charge',
        syncToInvoice: 'Sync to Invoice',
        syncToBill: 'Sync to Bill',
        jobCosting: 'Job Costing',
        noARCharges: 'No AR charges added yet. Click "Add AR Charge" to create one.',
        noAPCharges: 'No AP charges added yet. Click "Add AP Charge" to create one.',

        // Charge Table Headers
        sno: 'SNO',
        chargeDescription: 'Charge Description',
        currency: 'Currency',
        roe: 'ROE',
        basis: 'Basis',
        container: 'Container',
        quantity: 'Qty',
        billToParty: 'Bill To Party',
        refNumber: 'Ref #',
        sellRate: 'Sell Rate',
        buyRate: 'Buy Rate',
        ppcc: 'PP/CC',
        invoiceStatus: 'Invoice Status',
        billStatus: 'Bill Status',
        notInvoiced: 'Not Inv.',
        invoiced: 'Invoiced',
        notBilled: 'Not Billed',
        billed: 'Billed',

        // Rate Quote
        newRateQuote: 'New Rate Quote',
        quoteNumber: 'Quote Number',
        quoteDate: 'Quote Date',
        validUntil: 'Valid Until',
        origin: 'Origin',
        destination: 'Destination',
        commodity: 'Commodity',
        cargoReadyDate: 'Cargo Ready Date',
        transitTime: 'Transit Time',
        days: 'Days',
        freeDays: 'Free Days',
        demurrage: 'Demurrage',
        detention: 'Detention',
        totalCharges: 'Total Charges',
        selectCarrier: 'Select Carrier',
        addCarrier: 'Add Carrier',
        getQuote: 'Get Quote',
        saveQuote: 'Save Quote',
        sendQuote: 'Send Quote',

        // Print
        print: 'Print',
        printBooking: 'Print Booking',
        printInvoice: 'Print Invoice',
        printBill: 'Print Bill',
        printQuote: 'Print Quote',
        printReport: 'Print Report',
        printPreview: 'Print Preview',
        pageOf: 'Page {current} of {total}',
        printedOn: 'Printed on',
        preparedBy: 'Prepared by',
        termsAndConditions: 'Terms and Conditions',
        bankDetails: 'Bank Details',
        thankYou: 'Thank you for your business!',

        // Common Actions
        save: 'Save',
        saveBooking: 'Save Booking',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        close: 'Close',
        submit: 'Submit',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        import: 'Import',
        download: 'Download',
        upload: 'Upload',
        search: 'Search...',
        filter: 'Filter',
        clear: 'Clear',
        reset: 'Reset',
        apply: 'Apply',
        view: 'View',
        create: 'Create',
        update: 'Update',
        actions: 'Actions',
        moreFilters: 'More Filters',

        // Status
        loading: 'Loading...',
        noData: 'No data found',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        info: 'Information',
        active: 'Active',
        inactive: 'Inactive',
        completed: 'Completed',
        rejected: 'Rejected',
        cancelled: 'Cancelled',

        // User menu
        profile: 'Profile',
        myAccount: 'My Account',
        settings: 'Settings',
        logout: 'Logout',
        notifications: 'Notifications',
        messages: 'Messages',
        help: 'Help',

        // Form validation
        required: 'Required',
        requiredFields: 'Required fields must be filled',
        invalidEmail: 'Invalid email address',
        invalidPhone: 'Invalid phone number',

        // Confirmation messages
        confirmDelete: 'Are you sure you want to delete this item?',
        saveChanges: 'Do you want to save changes?',
        unsavedChanges: 'You have unsaved changes',
        deleteSuccess: 'Successfully deleted',
        saveSuccess: 'Successfully saved',
        updateSuccess: 'Successfully updated',
        errorOccurred: 'An error occurred',

        // Reports submenu
        reportsMenu: 'Reports',
        quotationReport: 'Quotation Report',
        accountingReport: 'Accounting Report',
        quotesBookings: 'Quotes & Bookings',

        // System Maintenance
        systemMaintenance: 'System Maintenance',
        location: 'Location',
        countryClause: 'Country Clause',
        clauses: 'Clauses',
        chargesCodeMaint: 'Charges Code',

        // Document types
        invoice: 'Invoice',
        creditNote: 'Credit Note',
        debitNote: 'Debit Note',
        bill: 'Bill',

        // Date/Time
        date: 'Date',
        time: 'Time',
        from: 'From',
        to: 'To',
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
        thisYear: 'This Year',

        // Numbers
        subtotal: 'Subtotal',
        tax: 'Tax',
        taxAmount: 'Tax Amount',
        total: 'Total',
        grandTotal: 'Grand Total',
        amountDue: 'Amount Due',
        amountPaid: 'Amount Paid',
        balanceDue: 'Balance Due',

        // Language
        language: 'Language',
        selectLanguage: 'Select Language',

        // Login Page
        signIn: 'Sign In',
        welcomeBack: 'Welcome back! Please enter your details to continue.',
        emailAddress: 'Email Address',
        enterUsername: 'Enter your username',
        password: 'Password',
        enterPassword: 'Enter your password',
        rememberMe: 'Remember me',
        forgotPassword: 'Forgot password?',
        signingIn: 'Signing in...',
        dontHaveAccount: "Don't have an account?",
        signUp: 'Sign up',
        loginSuccess: 'Login successful!',
        loginFailed: 'Login failed',
        aiCapabilities: 'AI Capabilities',
        aiPlatformDesc: 'Our TMS platform is fully AI-enabled with advanced capabilities.',
        see: 'See',
        visualAnalysis: 'Visual Analysis',
        speak: 'Speak',
        voiceOutput: 'Voice Output',
        hear: 'Hear',
        voiceInput: 'Voice Input',
        think: 'Think',
        aiAnalysis: 'AI Analysis',
        plan: 'Plan',
        smartPlanning: 'Smart Planning',
        contentGen: 'Content Gen',
        experienceRevolution: 'Experience The Revolution in Digital Freight Forwarding',
        centralizedAI: 'Centralized AI Powered Shipping Platform',

        // Shipment Management
        shipmentManagement: 'Shipment Management',
        trackAndOptimize: 'Track, manage, and optimize all your shipments with AI-powered insights',
        totalShipments: 'Total Shipments',
        fromLastMonth: 'from last month',
        onTrack: 'On track',
        arrivingToday: 'arriving today',
        reviewRequired: 'Review required',
        inTransit: 'In Transit',
        needsAttention: 'Needs Attention',
        delayed: 'Delayed',
        all: 'All',
        mostRecent: 'Most Recent',
        showingOf: 'Showing {shown} of {total} shipments',
        aiInsight: 'AI Insight',
    },
    es: {
        // Navigation
        aiInsights: 'Perspectivas IA',
        rateMatrix: 'Matriz de Tarifas',
        controlTower: 'Torre de Control',
        notificationCenter: 'Centro de Notificaciones',
        rateQuote: 'Cotización',
        rfq: 'Solicitud de Cotización',
        quoteFollowup: 'Seguimiento de Cotización',
        purchaseOrder: 'Orden de Compra',
        salesOrder: 'Orden de Venta',
        loadPlan: 'Plan de Carga',
        booking: 'Reserva',
        aiAutomation: 'Automatización IA',
        aiAgentSetup: 'Configuración Agente IA',
        api: 'API',
        visionBoard: 'Tablero de Visión',
        addressBook: 'Libreta de Direcciones',

        // Accounting Module
        accounting: 'Contabilidad',
        chartOfAccounts: 'Plan de Cuentas',
        manageGLAccounts: 'Gestione sus cuentas del libro mayor',
        chargeCodes: 'Códigos de Cargo',
        manageChargeCodes: 'Gestione códigos de cargo para facturación',
        customerInvoices: 'Facturas de Clientes',
        manageAR: 'Gestione cuentas por cobrar',
        vendorBills: 'Facturas de Proveedores',
        manageAP: 'Gestione cuentas por pagar',
        journalEntries: 'Asientos Contables',
        manageJournals: 'Gestione asientos contables',
        payments: 'Pagos',
        managePayments: 'Gestione pagos y recibos',
        reports: 'Informes',
        financialReports: 'Informes Financieros',
        analyticsReporting: 'Panel de análisis e informes',

        // Dashboard
        aiDrivenSummary: 'Resumen Impulsado por IA de Shipez',
        logisticsOverview: 'Resumen de Logística',
        aiSummary: 'Resumen IA',
        loadingAISummary: 'Cargando resumen de IA...',
        activeShipments: 'Envíos Activos',
        pendingQuotes: 'Cotizaciones Pendientes',
        inTransitContainer: 'Contenedor en Tránsito',
        revenue: 'Ingresos',
        fromLastMonth: 'del mes pasado',
        from: 'Desde',
        to: 'Hasta',
        create: 'Crear',

        // Chart of Accounts
        addAccount: 'Agregar Cuenta',
        editAccount: 'Editar Cuenta',
        accountCode: 'Código de Cuenta',
        accountName: 'Nombre de Cuenta',
        accountType: 'Tipo de Cuenta',
        subType: 'Subtipo',
        parentAccount: 'Cuenta Padre',
        description: 'Descripción',
        openingBalance: 'Saldo Inicial',
        currentBalance: 'Saldo Actual',
        isActive: 'Activo',
        asset: 'Activo',
        liability: 'Pasivo',
        equity: 'Patrimonio',
        income: 'Ingreso',
        expense: 'Gasto',
        cogs: 'Costo de Ventas',
        banks: 'Bancos',
        creditCards: 'Tarjetas de Crédito',
        allTypes: 'Todos los Tipos',
        allSubTypes: 'Todos los Subtipos',
        searchAccounts: 'Buscar cuentas...',
        noAccountsFound: 'No se encontraron cuentas. Haga clic en "Agregar Cuenta" para crear una.',

        // Charge Codes
        addChargeCode: 'Agregar Código de Cargo',
        editChargeCode: 'Editar Código de Cargo',
        chargeCode: 'Código de Cargo',
        chargeName: 'Nombre del Cargo',
        chargeCategory: 'Categoría',
        defaultRate: 'Tarifa Predeterminada',
        taxable: 'Gravable',
        glAccount: 'Cuenta Contable',
        searchChargeCodes: 'Buscar códigos de cargo...',
        noChargeCodesFound: 'No se encontraron códigos. Haga clic en "Agregar Código de Cargo" para crear uno.',

        // Customer Invoices
        newInvoice: 'Nueva Factura',
        editInvoice: 'Editar Factura',
        invoiceNumber: 'Factura #',
        invoiceDate: 'Fecha de Factura',
        dueDate: 'Fecha de Vencimiento',
        customer: 'Cliente',
        invoiceType: 'Tipo',
        amount: 'Monto',
        balance: 'Saldo',
        status: 'Estado',
        allInvoices: 'Todas las Facturas',
        draft: 'Borrador',
        issued: 'Emitida',
        partial: 'Parcial',
        paid: 'Pagada',
        void: 'Anulada',
        outstanding: 'Pendiente',
        totalOutstanding: 'Total Pendiente',
        searchInvoices: 'Buscar facturas...',
        allStatuses: 'Todos los Estados',
        noInvoicesFound: 'No se encontraron facturas. Haga clic en "Nueva Factura" para crear una.',

        // Vendor Bills
        newBill: 'Nueva Factura Proveedor',
        editBill: 'Editar Factura',
        billNumber: 'Factura #',
        billDate: 'Fecha de Factura',
        vendor: 'Proveedor',
        allBills: 'Todas las Facturas',
        pending: 'Pendiente',
        approved: 'Aprobada',
        searchBills: 'Buscar facturas...',
        noBillsFound: 'No se encontraron facturas. Haga clic en "Nueva Factura" para crear una.',

        // Journal Entries
        newJournal: 'Nuevo Asiento',
        editJournal: 'Editar Asiento',
        journalNumber: 'Asiento #',
        journalDate: 'Fecha',
        reference: 'Referencia',
        memo: 'Memo',
        debit: 'Débito',
        credit: 'Crédito',
        totalDebit: 'Total Débito',
        totalCredit: 'Total Crédito',
        balanced: 'Balanceado',
        unbalanced: 'Desbalanceado',
        posted: 'Publicado',
        searchJournals: 'Buscar asientos...',
        noJournalsFound: 'No se encontraron asientos. Haga clic en "Nuevo Asiento" para crear uno.',
        addLine: 'Agregar Línea',
        removeLine: 'Eliminar Línea',

        // Payments
        newPayment: 'Nuevo Pago',
        recordPayment: 'Registrar Pago',
        paymentNumber: 'Pago #',
        paymentDate: 'Fecha de Pago',
        paymentMethod: 'Método de Pago',
        paymentAmount: 'Monto',
        appliedTo: 'Aplicado a',
        cash: 'Efectivo',
        check: 'Cheque',
        bankTransfer: 'Transferencia Bancaria',
        creditCard: 'Tarjeta de Crédito',
        receipt: 'Recibo',
        payment: 'Pago',
        searchPayments: 'Buscar pagos...',
        noPaymentsFound: 'No se encontraron pagos. Haga clic en "Nuevo Pago" para registrar uno.',

        // Reports
        summary: 'Resumen',
        arAging: 'Antigüedad C/C',
        apAging: 'Antigüedad C/P',
        trialBalance: 'Balance de Comprobación',
        totalRevenue: 'Ingresos Totales',
        totalExpenses: 'Gastos Totales',
        netProfit: 'Ganancia Neta',
        pendingAR: 'C/C Pendiente',
        pendingAP: 'C/P Pendiente',
        totalInvoices: 'Total Facturas',
        paidInvoices: 'Facturas Pagadas',
        totalBills: 'Total Cuentas',
        paidBills: 'Cuentas Pagadas',
        export: 'Exportar',
        refresh: 'Actualizar',
        current: 'Actual',
        daysOverdue: '{days} Días',
        overdue: 'Vencido',

        // Booking Form
        customerBookingInfo: 'Información de Cliente y Reserva',
        editingBooking: 'Editando Reserva',
        newBookingTitle: 'Nueva Reserva',
        customerShipper: 'Cliente / Embarcador',
        contactName: 'Nombre de Contacto',
        telephone: 'Teléfono',
        email: 'Correo Electrónico',
        salesPerson: 'Vendedor',
        blMode: 'Modo BL',
        officeType: 'Tipo de Oficina',
        fileNumber: 'Número de Archivo',
        customerRef: 'Ref. Cliente #',
        carrierBookingNumber: 'Número de Reserva del Transportista',
        carrierBLNumber: 'Número BL del Transportista',
        bookingType: 'Tipo de Reserva',
        fileManager: 'Administrador de Archivo',
        coLoaderRefNo: 'Ref. Co-cargador',
        incoTerm: 'Incoterm',
        rateQuoteNumber: 'Número de Cotización',
        shippingType: 'Tipo de Envío',
        fcl: 'FCL',
        lcl: 'LCL',
        roRo: 'RO-RO',
        fullContainer: 'Contenedor Completo',
        lessContainer: 'Carga Suelta',
        rollOnRollOff: 'Roll-on/Roll-off',

        // Shipment Details
        shipmentDetails: 'Detalles del Envío',
        consignee: 'Consignatario',
        notifyParty: 'Parte a Notificar',
        carrier: 'Transportista',
        vessel: 'Embarcación',
        voyage: 'Viaje',
        portOfLoading: 'Puerto de Carga',
        portOfDischarge: 'Puerto de Descarga',
        placeOfDelivery: 'Lugar de Entrega',
        etd: 'ETD',
        eta: 'ETA',

        // Container Details
        containerDetails: 'Detalles del Contenedor',
        containerNumber: 'Número de Contenedor',
        containerType: 'Tipo de Contenedor',
        sealNumber: 'Número de Sello',
        weight: 'Peso',
        volume: 'Volumen',
        pieces: 'Piezas',
        packageType: 'Tipo de Empaque',

        // Cargo Details
        cargoDetails: 'Detalles de Carga',
        cargoDescription: 'Descripción de Carga',
        hsCode: 'Código HS',
        grossWeight: 'Peso Bruto',
        netWeight: 'Peso Neto',
        cbm: 'CBM',

        // Print Templates
        downloadPDF: 'Descargar PDF',
        print: 'Imprimir',
        loadingQuotation: 'Cargando cotización...',
        customer: 'Cliente',
        rqNumber: 'N° COTIZACIÓN',
        effectiveDate: 'FECHA EFECTIVA',
        expiryDate: 'FECHA VENCIMIENTO',
        quotedBy: 'COTIZADO POR',
        salesPerson: 'VENDEDOR',
        routingInstruction: 'Instrucción de Ruta',
        chargesBreakdown: 'Desglose de Cargos',
        remarks: 'Observaciones',
        subtotal: 'Subtotal',
        grandTotal: 'Total General',
        termsAndConditions: 'Términos y Condiciones',
        preparedBy: 'Preparado Por',
        authorizedSignature: 'Firma Autorizada',
        thankYouForBusiness: '¡Gracias por su preferencia!',

        // AR/AP Section
        arApInformation: 'Información C/C y C/P',
        manageRevenueAndCost: 'Gestione cargos de ingresos y costos',
        accountsReceivable: 'Cuentas por Cobrar',
        accountsPayable: 'Cuentas por Pagar',
        revenue: 'Ingresos',
        costs: 'Costos',
        totalAR: 'Total C/C',
        totalAP: 'Total C/P',
        profit: 'Ganancia',
        margin: 'Margen',
        addARCharge: 'Agregar Cargo C/C',
        addAPCharge: 'Agregar Cargo C/P',
        syncToInvoice: 'Sincronizar a Factura',
        syncToBill: 'Sincronizar a Cuenta',
        jobCosting: 'Costeo de Trabajo',
        noARCharges: 'No hay cargos C/C. Haga clic en "Agregar Cargo C/C" para crear uno.',
        noAPCharges: 'No hay cargos C/P. Haga clic en "Agregar Cargo C/P" para crear uno.',

        // Charge Table Headers
        sno: 'N°',
        chargeDescription: 'Descripción del Cargo',
        currency: 'Moneda',
        roe: 'T/C',
        basis: 'Base',
        container: 'Contenedor',
        quantity: 'Cant.',
        billToParty: 'Facturar a',
        refNumber: 'Ref #',
        sellRate: 'Precio Venta',
        buyRate: 'Precio Compra',
        ppcc: 'PP/CC',
        invoiceStatus: 'Estado Factura',
        billStatus: 'Estado Cuenta',
        notInvoiced: 'Sin Fact.',
        invoiced: 'Facturado',
        notBilled: 'Sin Cuenta',
        billed: 'Contabilizado',

        // Rate Quote
        newRateQuote: 'Nueva Cotización',
        quoteNumber: 'Número de Cotización',
        quoteDate: 'Fecha de Cotización',
        validUntil: 'Válido Hasta',
        origin: 'Origen',
        destination: 'Destino',
        commodity: 'Mercancía',
        cargoReadyDate: 'Fecha Lista Carga',
        transitTime: 'Tiempo de Tránsito',
        days: 'Días',
        freeDays: 'Días Libres',
        demurrage: 'Demora',
        detention: 'Detención',
        totalCharges: 'Cargos Totales',
        selectCarrier: 'Seleccionar Transportista',
        addCarrier: 'Agregar Transportista',
        getQuote: 'Obtener Cotización',
        saveQuote: 'Guardar Cotización',
        sendQuote: 'Enviar Cotización',

        // Print
        print: 'Imprimir',
        printBooking: 'Imprimir Reserva',
        printInvoice: 'Imprimir Factura',
        printBill: 'Imprimir Cuenta',
        printQuote: 'Imprimir Cotización',
        printReport: 'Imprimir Informe',
        printPreview: 'Vista Previa',
        pageOf: 'Página {current} de {total}',
        printedOn: 'Impreso el',
        preparedBy: 'Preparado por',
        termsAndConditions: 'Términos y Condiciones',
        bankDetails: 'Datos Bancarios',
        thankYou: '¡Gracias por su preferencia!',

        // Common Actions
        save: 'Guardar',
        saveBooking: 'Guardar Reserva',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        add: 'Añadir',
        close: 'Cerrar',
        submit: 'Enviar',
        confirm: 'Confirmar',
        back: 'Atrás',
        next: 'Siguiente',
        import: 'Importar',
        download: 'Descargar',
        upload: 'Subir',
        search: 'Buscar...',
        filter: 'Filtrar',
        clear: 'Limpiar',
        reset: 'Restablecer',
        apply: 'Aplicar',
        view: 'Ver',
        create: 'Crear',
        update: 'Actualizar',
        actions: 'Acciones',
        moreFilters: 'Más Filtros',

        // Status
        loading: 'Cargando...',
        noData: 'No se encontraron datos',
        success: 'Éxito',
        error: 'Error',
        warning: 'Advertencia',
        info: 'Información',
        active: 'Activo',
        inactive: 'Inactivo',
        completed: 'Completado',
        rejected: 'Rechazado',
        cancelled: 'Cancelado',

        // User menu
        profile: 'Perfil',
        myAccount: 'Mi Cuenta',
        settings: 'Configuración',
        logout: 'Cerrar Sesión',
        notifications: 'Notificaciones',
        messages: 'Mensajes',
        help: 'Ayuda',

        // Form validation
        required: 'Requerido',
        requiredFields: 'Los campos requeridos deben ser completados',
        invalidEmail: 'Correo electrónico inválido',
        invalidPhone: 'Número de teléfono inválido',

        // Confirmation messages
        confirmDelete: '¿Está seguro de que desea eliminar este elemento?',
        saveChanges: '¿Desea guardar los cambios?',
        unsavedChanges: 'Tiene cambios sin guardar',
        deleteSuccess: 'Eliminado exitosamente',
        saveSuccess: 'Guardado exitosamente',
        updateSuccess: 'Actualizado exitosamente',
        errorOccurred: 'Ocurrió un error',

        // Reports submenu
        reportsMenu: 'Informes',
        quotationReport: 'Informe de Cotización',
        accountingReport: 'Informe Contable',
        quotesBookings: 'Cotizaciones y Reservas',

        // System Maintenance
        systemMaintenance: 'Mantenimiento del Sistema',
        vessel: 'Embarcación',
        location: 'Ubicación',
        countryClause: 'Cláusula de País',
        clauses: 'Cláusulas',
        chargesCodeMaint: 'Código de Cargos',

        // Document types
        invoice: 'Factura',
        creditNote: 'Nota de Crédito',
        debitNote: 'Nota de Débito',
        bill: 'Cuenta',

        // Date/Time
        date: 'Fecha',
        time: 'Hora',
        from: 'Desde',
        to: 'Hasta',
        today: 'Hoy',
        yesterday: 'Ayer',
        thisWeek: 'Esta Semana',
        thisMonth: 'Este Mes',
        lastMonth: 'Mes Pasado',
        thisYear: 'Este Año',

        // Numbers
        subtotal: 'Subtotal',
        tax: 'Impuesto',
        taxAmount: 'Monto de Impuesto',
        total: 'Total',
        grandTotal: 'Total General',
        amountDue: 'Monto Adeudado',
        amountPaid: 'Monto Pagado',
        balanceDue: 'Saldo Pendiente',

        // Language
        language: 'Idioma',
        selectLanguage: 'Seleccionar Idioma',

        // Login Page
        signIn: 'Iniciar Sesión',
        welcomeBack: '¡Bienvenido! Por favor ingrese sus datos para continuar.',
        emailAddress: 'Dirección de Correo',
        enterUsername: 'Ingrese su nombre de usuario',
        password: 'Contraseña',
        enterPassword: 'Ingrese su contraseña',
        rememberMe: 'Recuérdame',
        forgotPassword: '¿Olvidó su contraseña?',
        signingIn: 'Iniciando sesión...',
        dontHaveAccount: '¿No tiene una cuenta?',
        signUp: 'Registrarse',
        loginSuccess: '¡Inicio de sesión exitoso!',
        loginFailed: 'Error de inicio de sesión',
        aiCapabilities: 'Capacidades de IA',
        aiPlatformDesc: 'Nuestra plataforma TMS está totalmente habilitada con IA con capacidades avanzadas.',
        see: 'Ver',
        visualAnalysis: 'Análisis Visual',
        speak: 'Hablar',
        voiceOutput: 'Salida de Voz',
        hear: 'Escuchar',
        voiceInput: 'Entrada de Voz',
        think: 'Pensar',
        aiAnalysis: 'Análisis IA',
        plan: 'Planificar',
        smartPlanning: 'Planificación Inteligente',
        contentGen: 'Generación de Contenido',
        experienceRevolution: 'Experimente la Revolución en el Transporte Digital de Carga',
        centralizedAI: 'Plataforma de Envío Centralizada con IA',

        // Shipment Management
        shipmentManagement: 'Gestión de Envíos',
        trackAndOptimize: 'Rastree, gestione y optimice todos sus envíos con perspectivas de IA',
        totalShipments: 'Envíos Totales',
        fromLastMonth: 'del mes pasado',
        onTrack: 'A tiempo',
        arrivingToday: 'llegando hoy',
        reviewRequired: 'Revisión requerida',
        inTransit: 'En Tránsito',
        needsAttention: 'Requiere Atención',
        delayed: 'Retrasado',
        all: 'Todos',
        mostRecent: 'Más Recientes',
        showingOf: 'Mostrando {shown} de {total} envíos',
        aiInsight: 'Perspectiva IA',
    },
    zh: {
        // Navigation
        aiInsights: 'AI洞察',
        rateMatrix: '费率矩阵',
        controlTower: '控制塔',
        notificationCenter: '通知中心',
        rateQuote: '报价',
        rfq: '询价',
        quoteFollowup: '报价跟进',
        purchaseOrder: '采购订单',
        salesOrder: '销售订单',
        loadPlan: '装载计划',
        booking: '预订',
        aiAutomation: 'AI自动化',
        aiAgentSetup: 'AI代理设置',
        api: 'API',
        visionBoard: '愿景板',
        addressBook: '地址簿',

        // Accounting
        accounting: '会计',
        chartOfAccounts: '会计科目表',
        manageGLAccounts: '管理您的总账科目',
        chargeCodes: '费用代码',
        manageChargeCodes: '管理计费费用代码',
        customerInvoices: '客户发票',
        manageAR: '管理应收账款',
        vendorBills: '供应商账单',
        manageAP: '管理应付账款',
        journalEntries: '日记账分录',
        manageJournals: '管理日记账分录',
        payments: '付款',
        managePayments: '管理付款和收款',
        reports: '报告',
        financialReports: '财务报告',
        analyticsReporting: '分析和报告仪表板',

        // Dashboard
        aiDrivenSummary: 'Shipez AI驱动摘要',
        logisticsOverview: '物流概览',
        aiSummary: 'AI摘要',
        loadingAISummary: '正在加载AI摘要...',
        activeShipments: '活跃货运',
        pendingQuotes: '待处理报价',
        inTransitContainer: '在途集装箱',
        revenue: '收入',
        fromLastMonth: '较上月',
        from: '从',
        to: '至',
        create: '创建',

        // Login
        signIn: '登录',
        welcomeBack: '欢迎回来！请输入您的信息。',
        emailAddress: '邮箱地址',
        enterUsername: '输入用户名',
        password: '密码',
        enterPassword: '输入密码',
        rememberMe: '记住我',
        forgotPassword: '忘记密码？',
        signingIn: '登录中...',
        dontHaveAccount: '没有账户？',
        signUp: '注册',
        aiCapabilities: 'AI能力',
        aiPlatformDesc: '我们的TMS平台完全支持AI高级功能。',
        see: '看',
        visualAnalysis: '视觉分析',
        speak: '说',
        voiceOutput: '语音输出',
        hear: '听',
        voiceInput: '语音输入',
        think: '想',
        aiAnalysis: 'AI分析',
        plan: '计划',
        smartPlanning: '智能规划',
        contentGen: '内容生成',
        experienceRevolution: '体验数字货运革命',
        centralizedAI: 'AI驱动的集中式运输平台',

        // Chart of Accounts
        addAccount: '添加科目',
        editAccount: '编辑科目',
        accountCode: '科目代码',
        accountName: '科目名称',
        accountType: '科目类型',
        subType: '子类型',
        parentAccount: '父科目',
        description: '描述',
        openingBalance: '期初余额',
        currentBalance: '当前余额',
        isActive: '启用',
        asset: '资产',
        liability: '负债',
        equity: '权益',
        income: '收入',
        expense: '费用',
        cogs: '销售成本',
        banks: '银行',
        creditCards: '信用卡',
        allTypes: '所有类型',
        allSubTypes: '所有子类型',
        searchAccounts: '搜索科目...',
        noAccountsFound: '未找到科目。点击"添加科目"创建一个。',

        // Common
        save: '保存',
        cancel: '取消',
        delete: '删除',
        edit: '编辑',
        add: '添加',
        close: '关闭',
        submit: '提交',
        confirm: '确认',
        back: '返回',
        next: '下一步',
        print: '打印',
        export: '导出',
        import: '导入',
        download: '下载',
        upload: '上传',
        refresh: '刷新',
        search: '搜索...',
        filter: '筛选',
        clear: '清除',
        reset: '重置',
        apply: '应用',
        view: '查看',
        create: '创建',
        update: '更新',
        actions: '操作',

        // Status
        loading: '加载中...',
        noData: '未找到数据',
        status: '状态',
        active: '活跃',
        inactive: '非活跃',
        draft: '草稿',
        issued: '已发出',
        paid: '已付款',
        pending: '待处理',
        approved: '已批准',

        // User menu
        profile: '个人资料',
        myAccount: '我的账户',
        settings: '设置',
        logout: '退出登录',
        notifications: '通知',

        // AR/AP
        arApInformation: '应收/应付信息',
        accountsReceivable: '应收账款',
        accountsPayable: '应付账款',
        totalAR: '应收总额',
        totalAP: '应付总额',
        profit: '利润',
        margin: '利润率',
        addARCharge: '添加应收费用',
        addAPCharge: '添加应付费用',
        syncToInvoice: '同步到发票',
        syncToBill: '同步到账单',
        jobCosting: '作业成本',

        // Print
        printBooking: '打印预订',
        printInvoice: '打印发票',
        printReport: '打印报告',
        printPreview: '打印预览',
        pageOf: '第{current}页，共{total}页',
        printedOn: '打印日期',
        preparedBy: '编制人',

        // Booking
        newBookingTitle: '新建预订',
        saveBooking: '保存预订',
        customerShipper: '客户/发货人',

        // Reports
        summary: '摘要',
        arAging: '应收账龄',
        apAging: '应付账龄',
        trialBalance: '试算平衡表',
        totalRevenue: '总收入',
        totalExpenses: '总支出',
        netProfit: '净利润',

        // System
        systemMaintenance: '系统维护',
        reportsMenu: '报告',

        language: '语言',
        selectLanguage: '选择语言',
    },
    fr: {
        // Navigation
        aiInsights: 'Perspectives IA',
        rateMatrix: 'Matrice des Tarifs',
        controlTower: 'Tour de Contrôle',
        notificationCenter: 'Centre de Notifications',
        rateQuote: 'Devis',
        rfq: 'Demande de Devis',
        quoteFollowup: 'Suivi des Devis',
        purchaseOrder: 'Bon de Commande',
        salesOrder: 'Ordre de Vente',
        loadPlan: 'Plan de Chargement',
        booking: 'Réservation',
        aiAutomation: 'Automatisation IA',
        aiAgentSetup: 'Configuration Agent IA',
        api: 'API',
        visionBoard: 'Tableau de Vision',
        addressBook: 'Carnet d\'Adresses',

        // Accounting
        accounting: 'Comptabilité',
        chartOfAccounts: 'Plan Comptable',
        manageGLAccounts: 'Gérez vos comptes du grand livre',
        chargeCodes: 'Codes de Frais',
        customerInvoices: 'Factures Clients',
        vendorBills: 'Factures Fournisseurs',
        journalEntries: 'Écritures Comptables',
        payments: 'Paiements',
        reports: 'Rapports',
        financialReports: 'Rapports Financiers',

        // Dashboard
        aiDrivenSummary: 'Résumé IA Shipez',
        logisticsOverview: 'Aperçu Logistique',
        aiSummary: 'Résumé IA',
        loadingAISummary: 'Chargement du résumé IA...',
        activeShipments: 'Expéditions Actives',
        pendingQuotes: 'Devis en Attente',
        inTransitContainer: 'Conteneur en Transit',
        revenue: 'Revenus',
        fromLastMonth: 'du mois dernier',
        from: 'De',
        to: 'À',
        create: 'Créer',

        // Login
        signIn: 'Connexion',
        welcomeBack: 'Bienvenue! Veuillez entrer vos informations.',
        emailAddress: 'Adresse Email',
        enterUsername: 'Entrez votre nom d\'utilisateur',
        password: 'Mot de passe',
        enterPassword: 'Entrez votre mot de passe',
        rememberMe: 'Se souvenir de moi',
        forgotPassword: 'Mot de passe oublié?',
        signingIn: 'Connexion en cours...',
        dontHaveAccount: 'Vous n\'avez pas de compte?',
        signUp: 'S\'inscrire',
        aiCapabilities: 'Capacités IA',
        aiPlatformDesc: 'Notre plateforme TMS est entièrement compatible avec l\'IA.',
        see: 'Voir',
        visualAnalysis: 'Analyse Visuelle',
        speak: 'Parler',
        voiceOutput: 'Sortie Vocale',
        hear: 'Entendre',
        voiceInput: 'Entrée Vocale',
        think: 'Penser',
        aiAnalysis: 'Analyse IA',
        plan: 'Planifier',
        smartPlanning: 'Planification Intelligente',
        contentGen: 'Génération de Contenu',
        experienceRevolution: 'Découvrez la Révolution du Fret Numérique',
        centralizedAI: 'Plateforme d\'Expédition Centralisée avec IA',

        // Common
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        add: 'Ajouter',
        close: 'Fermer',
        submit: 'Soumettre',
        confirm: 'Confirmer',
        back: 'Retour',
        next: 'Suivant',
        print: 'Imprimer',
        export: 'Exporter',
        import: 'Importer',
        refresh: 'Actualiser',
        search: 'Rechercher...',
        filter: 'Filtrer',

        // Status
        loading: 'Chargement...',
        noData: 'Aucune donnée trouvée',
        status: 'Statut',
        draft: 'Brouillon',
        issued: 'Émis',
        paid: 'Payé',
        pending: 'En attente',

        // User menu
        profile: 'Profil',
        settings: 'Paramètres',
        logout: 'Déconnexion',
        notifications: 'Notifications',

        // AR/AP
        arApInformation: 'Information C/R et C/P',
        accountsReceivable: 'Comptes à Recevoir',
        accountsPayable: 'Comptes à Payer',
        totalAR: 'Total C/R',
        totalAP: 'Total C/P',
        profit: 'Bénéfice',
        margin: 'Marge',

        // Print
        printBooking: 'Imprimer Réservation',
        printInvoice: 'Imprimer Facture',
        pageOf: 'Page {current} sur {total}',
        printedOn: 'Imprimé le',

        // Reports
        summary: 'Résumé',
        trialBalance: 'Balance Générale',
        totalRevenue: 'Revenus Totaux',
        totalExpenses: 'Dépenses Totales',
        netProfit: 'Bénéfice Net',

        systemMaintenance: 'Maintenance Système',
        reportsMenu: 'Rapports',

        language: 'Langue',
        selectLanguage: 'Sélectionner la Langue',
    },
    ar: {
        // Navigation
        aiInsights: 'رؤى الذكاء الاصطناعي',
        rateMatrix: 'مصفوفة الأسعار',
        controlTower: 'برج التحكم',
        notificationCenter: 'مركز الإشعارات',
        rateQuote: 'عرض السعر',
        rfq: 'طلب عرض سعر',
        quoteFollowup: 'متابعة العروض',
        purchaseOrder: 'أمر شراء',
        salesOrder: 'أمر بيع',
        loadPlan: 'خطة التحميل',
        booking: 'الحجز',
        aiAutomation: 'أتمتة الذكاء الاصطناعي',
        aiAgentSetup: 'إعداد وكيل الذكاء الاصطناعي',
        api: 'واجهة برمجة التطبيقات',
        addressBook: 'دفتر العناوين',

        // Accounting
        accounting: 'المحاسبة',
        chartOfAccounts: 'دليل الحسابات',
        chargeCodes: 'رموز الرسوم',
        customerInvoices: 'فواتير العملاء',
        vendorBills: 'فواتير الموردين',
        journalEntries: 'قيود اليومية',
        payments: 'المدفوعات',
        reports: 'التقارير',

        // Dashboard
        aiDrivenSummary: 'ملخص شيبز بالذكاء الاصطناعي',
        logisticsOverview: 'نظرة عامة على الخدمات اللوجستية',
        aiSummary: 'ملخص الذكاء الاصطناعي',
        loadingAISummary: 'جاري تحميل ملخص الذكاء الاصطناعي...',
        activeShipments: 'الشحنات النشطة',
        pendingQuotes: 'العروض المعلقة',
        inTransitContainer: 'الحاويات قيد النقل',
        revenue: 'الإيرادات',
        fromLastMonth: 'من الشهر الماضي',
        from: 'من',
        to: 'إلى',
        create: 'إنشاء',

        // Login
        signIn: 'تسجيل الدخول',
        welcomeBack: 'مرحباً بعودتك! يرجى إدخال بياناتك.',
        emailAddress: 'البريد الإلكتروني',
        enterUsername: 'أدخل اسم المستخدم',
        password: 'كلمة المرور',
        enterPassword: 'أدخل كلمة المرور',
        rememberMe: 'تذكرني',
        forgotPassword: 'نسيت كلمة المرور؟',
        signingIn: 'جاري تسجيل الدخول...',
        dontHaveAccount: 'ليس لديك حساب؟',
        signUp: 'إنشاء حساب',
        aiCapabilities: 'قدرات الذكاء الاصطناعي',
        aiPlatformDesc: 'منصة TMS الخاصة بنا مدعومة بالكامل بالذكاء الاصطناعي.',
        see: 'رؤية',
        visualAnalysis: 'التحليل البصري',
        speak: 'تحدث',
        voiceOutput: 'مخرجات صوتية',
        hear: 'استمع',
        voiceInput: 'مدخلات صوتية',
        think: 'فكر',
        aiAnalysis: 'تحليل الذكاء الاصطناعي',
        plan: 'خطط',
        smartPlanning: 'التخطيط الذكي',
        contentGen: 'توليد المحتوى',
        experienceRevolution: 'اختبر ثورة الشحن الرقمي',
        centralizedAI: 'منصة شحن مركزية مدعومة بالذكاء الاصطناعي',

        // Common
        save: 'حفظ',
        cancel: 'إلغاء',
        delete: 'حذف',
        edit: 'تعديل',
        add: 'إضافة',
        close: 'إغلاق',
        print: 'طباعة',
        export: 'تصدير',
        refresh: 'تحديث',
        search: 'بحث...',

        // Status
        loading: 'جاري التحميل...',
        noData: 'لم يتم العثور على بيانات',

        // User menu
        profile: 'الملف الشخصي',
        settings: 'الإعدادات',
        logout: 'تسجيل الخروج',

        // AR/AP
        arApInformation: 'معلومات المستحقات والمطلوبات',
        accountsReceivable: 'حسابات القبض',
        accountsPayable: 'حسابات الدفع',

        systemMaintenance: 'صيانة النظام',
        reportsMenu: 'التقارير',

        language: 'اللغة',
        selectLanguage: 'اختر اللغة',
    },
    de: {
        // Navigation
        aiInsights: 'KI-Einblicke',
        rateMatrix: 'Tarifmatrix',
        controlTower: 'Kontrollturm',
        notificationCenter: 'Benachrichtigungszentrum',
        rateQuote: 'Preisangebot',
        rfq: 'Angebotsanfrage',
        quoteFollowup: 'Angebotsverfolgung',
        purchaseOrder: 'Bestellung',
        salesOrder: 'Verkaufsauftrag',
        loadPlan: 'Ladeplan',
        booking: 'Buchung',
        aiAutomation: 'KI-Automatisierung',
        aiAgentSetup: 'KI-Agent-Setup',
        api: 'API',
        addressBook: 'Adressbuch',

        // Accounting
        accounting: 'Buchhaltung',
        chartOfAccounts: 'Kontenplan',
        chargeCodes: 'Gebührencodes',
        customerInvoices: 'Kundenrechnungen',
        vendorBills: 'Lieferantenrechnungen',
        journalEntries: 'Journalbuchungen',
        payments: 'Zahlungen',
        reports: 'Berichte',

        // Dashboard
        aiDrivenSummary: 'Shipez KI-gesteuerte Zusammenfassung',
        logisticsOverview: 'Logistikübersicht',
        aiSummary: 'KI-Zusammenfassung',
        loadingAISummary: 'KI-Zusammenfassung wird geladen...',
        activeShipments: 'Aktive Sendungen',
        pendingQuotes: 'Ausstehende Angebote',
        inTransitContainer: 'Container unterwegs',
        revenue: 'Umsatz',
        fromLastMonth: 'vom letzten Monat',
        from: 'Von',
        to: 'Bis',
        create: 'Erstellen',

        // Login
        signIn: 'Anmelden',
        welcomeBack: 'Willkommen zurück! Bitte geben Sie Ihre Daten ein.',
        emailAddress: 'E-Mail-Adresse',
        enterUsername: 'Benutzername eingeben',
        password: 'Passwort',
        enterPassword: 'Passwort eingeben',
        rememberMe: 'Angemeldet bleiben',
        forgotPassword: 'Passwort vergessen?',
        signingIn: 'Anmelden...',
        dontHaveAccount: 'Kein Konto?',
        signUp: 'Registrieren',
        aiCapabilities: 'KI-Fähigkeiten',
        aiPlatformDesc: 'Unsere TMS-Plattform ist vollständig KI-fähig.',
        see: 'Sehen',
        visualAnalysis: 'Visuelle Analyse',
        speak: 'Sprechen',
        voiceOutput: 'Sprachausgabe',
        hear: 'Hören',
        voiceInput: 'Spracheingabe',
        think: 'Denken',
        aiAnalysis: 'KI-Analyse',
        plan: 'Planen',
        smartPlanning: 'Intelligente Planung',
        contentGen: 'Inhaltsgenerierung',
        experienceRevolution: 'Erleben Sie die Revolution der digitalen Fracht',
        centralizedAI: 'KI-gestützte zentrale Versandplattform',

        // Common
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        add: 'Hinzufügen',
        close: 'Schließen',
        print: 'Drucken',
        export: 'Exportieren',
        refresh: 'Aktualisieren',
        search: 'Suchen...',

        // Status
        loading: 'Laden...',
        noData: 'Keine Daten gefunden',

        // User menu
        profile: 'Profil',
        settings: 'Einstellungen',
        logout: 'Abmelden',

        systemMaintenance: 'Systemwartung',
        reportsMenu: 'Berichte',

        language: 'Sprache',
        selectLanguage: 'Sprache auswählen',
    },
    hi: {
        // Navigation
        aiInsights: 'एआई अंतर्दृष्टि',
        rateMatrix: 'दर मैट्रिक्स',
        controlTower: 'नियंत्रण टॉवर',
        notificationCenter: 'अधिसूचना केंद्र',
        rateQuote: 'दर उद्धरण',
        rfq: 'आरएफक्यू',
        quoteFollowup: 'उद्धरण अनुवर्ती',
        purchaseOrder: 'क्रय आदेश',
        salesOrder: 'विक्रय आदेश',
        loadPlan: 'लोड योजना',
        booking: 'बुकिंग',
        aiAutomation: 'एआई स्वचालन',
        aiAgentSetup: 'एआई एजेंट सेटअप',
        api: 'एपीआई',
        visionBoard: 'विजन बोर्ड',
        addressBook: 'पता पुस्तिका',

        // Accounting Module
        accounting: 'लेखांकन',
        chartOfAccounts: 'खातों का चार्ट',
        manageGLAccounts: 'अपने सामान्य खाता बही खातों का प्रबंधन करें',
        chargeCodes: 'प्रभार कोड',
        manageChargeCodes: 'बिलिंग के लिए प्रभार कोड प्रबंधित करें',
        customerInvoices: 'ग्राहक चालान',
        manageAR: 'प्राप्य खातों का प्रबंधन करें',
        vendorBills: 'विक्रेता बिल',
        manageAP: 'देय खातों का प्रबंधन करें',
        journalEntries: 'जर्नल प्रविष्टियाँ',
        manageJournals: 'जर्नल प्रविष्टियाँ प्रबंधित करें',
        payments: 'भुगतान',
        managePayments: 'भुगतान और रसीदें प्रबंधित करें',
        reports: 'रिपोर्ट',
        financialReports: 'वित्तीय रिपोर्ट',
        analyticsReporting: 'विश्लेषण और रिपोर्टिंग डैशबोर्ड',

        // Dashboard
        aiDrivenSummary: 'शिपेज़ एआई-संचालित सारांश',
        logisticsOverview: 'रसद अवलोकन',
        aiSummary: 'एआई सारांश',
        loadingAISummary: 'एआई सारांश लोड हो रहा है...',
        activeShipments: 'सक्रिय शिपमेंट',
        pendingQuotes: 'लंबित उद्धरण',
        inTransitContainer: 'पारगमन में कंटेनर',
        revenue: 'राजस्व',
        fromLastMonth: 'पिछले महीने से',
        from: 'से',
        to: 'तक',
        create: 'बनाएं',

        // Chart of Accounts Page
        addAccount: 'खाता जोड़ें',
        editAccount: 'खाता संपादित करें',
        accountCode: 'खाता कोड',
        accountName: 'खाता नाम',
        accountType: 'खाता प्रकार',
        subType: 'उप-प्रकार',
        parentAccount: 'मूल खाता',
        description: 'विवरण',
        openingBalance: 'प्रारंभिक शेष',
        currentBalance: 'वर्तमान शेष',
        isActive: 'सक्रिय है',
        asset: 'संपत्ति',
        liability: 'देयता',
        equity: 'इक्विटी',
        income: 'आय',
        expense: 'व्यय',
        cogs: 'बेचे गए माल की लागत',
        banks: 'बैंक',
        creditCards: 'क्रेडिट कार्ड',
        allTypes: 'सभी प्रकार',
        allSubTypes: 'सभी उप-प्रकार',
        searchAccounts: 'खाते खोजें...',
        noAccountsFound: 'कोई खाता नहीं मिला। एक बनाने के लिए "खाता जोड़ें" पर क्लिक करें।',

        // Charge Codes Page
        addChargeCode: 'प्रभार कोड जोड़ें',
        editChargeCode: 'प्रभार कोड संपादित करें',
        chargeCode: 'प्रभार कोड',
        chargeName: 'प्रभार नाम',
        chargeCategory: 'श्रेणी',
        defaultRate: 'डिफ़ॉल्ट दर',
        taxable: 'कर योग्य',
        glAccount: 'सामान्य खाता बही खाता',
        searchChargeCodes: 'प्रभार कोड खोजें...',
        noChargeCodesFound: 'कोई प्रभार कोड नहीं मिला। एक बनाने के लिए "प्रभार कोड जोड़ें" पर क्लिक करें।',

        // Customer Invoices Page
        newInvoice: 'नया चालान',
        editInvoice: 'चालान संपादित करें',
        invoiceNumber: 'चालान #',
        invoiceDate: 'चालान तिथि',
        dueDate: 'नियत तिथि',
        customer: 'ग्राहक',
        invoiceType: 'प्रकार',
        amount: 'राशि',
        balance: 'शेष',
        status: 'स्थिति',
        allInvoices: 'सभी चालान',
        draft: 'मसौदा',
        issued: 'जारी किया गया',
        partial: 'आंशिक',
        paid: 'भुगतान किया',
        void: 'अमान्य',
        outstanding: 'बकाया',
        totalOutstanding: 'कुल बकाया',
        searchInvoices: 'चालान खोजें...',
        allStatuses: 'सभी स्थितियाँ',
        noInvoicesFound: 'कोई चालान नहीं मिला। एक बनाने के लिए "नया चालान" पर क्लिक करें।',

        // Vendor Bills Page
        newBill: 'नया बिल',
        editBill: 'बिल संपादित करें',
        billNumber: 'बिल #',
        billDate: 'बिल तिथि',
        vendor: 'विक्रेता',
        allBills: 'सभी बिल',
        pending: 'लंबित',
        approved: 'स्वीकृत',
        searchBills: 'बिल खोजें...',
        noBillsFound: 'कोई बिल नहीं मिला। एक बनाने के लिए "नया बिल" पर क्लिक करें।',

        // Journal Entries Page
        newJournal: 'नई जर्नल प्रविष्टि',
        editJournal: 'जर्नल प्रविष्टि संपादित करें',
        journalNumber: 'जर्नल #',
        journalDate: 'तिथि',
        reference: 'संदर्भ',
        memo: 'मेमो',
        debit: 'डेबिट',
        credit: 'क्रेडिट',
        totalDebit: 'कुल डेबिट',
        totalCredit: 'कुल क्रेडिट',
        balanced: 'संतुलित',
        unbalanced: 'असंतुलित',
        posted: 'पोस्ट किया गया',
        searchJournals: 'जर्नल प्रविष्टियाँ खोजें...',
        noJournalsFound: 'कोई जर्नल प्रविष्टि नहीं मिली। एक बनाने के लिए "नई जर्नल प्रविष्टि" पर क्लिक करें।',
        addLine: 'पंक्ति जोड़ें',
        removeLine: 'पंक्ति हटाएं',

        // Payments Page
        newPayment: 'नया भुगतान',
        recordPayment: 'भुगतान रिकॉर्ड करें',
        paymentNumber: 'भुगतान #',
        paymentDate: 'भुगतान तिथि',
        paymentMethod: 'भुगतान विधि',
        paymentAmount: 'राशि',
        appliedTo: 'लागू किया गया',
        cash: 'नकद',
        check: 'चेक',
        bankTransfer: 'बैंक स्थानांतरण',
        creditCard: 'क्रेडिट कार्ड',
        receipt: 'रसीद',
        payment: 'भुगतान',
        searchPayments: 'भुगतान खोजें...',
        noPaymentsFound: 'कोई भुगतान नहीं मिला। एक रिकॉर्ड करने के लिए "नया भुगतान" पर क्लिक करें।',

        // Reports Page
        summary: 'सारांश',
        arAging: 'प्राप्य खाते आयु',
        apAging: 'देय खाते आयु',
        trialBalance: 'परीक्षण शेष',
        totalRevenue: 'कुल राजस्व',
        totalExpenses: 'कुल व्यय',
        netProfit: 'शुद्ध लाभ',
        pendingAR: 'लंबित प्राप्य',
        pendingAP: 'लंबित देय',
        totalInvoices: 'कुल चालान',
        paidInvoices: 'भुगतान किए गए चालान',
        totalBills: 'कुल बिल',
        paidBills: 'भुगतान किए गए बिल',
        export: 'निर्यात',
        refresh: 'ताज़ा करें',
        current: 'वर्तमान',
        daysOverdue: '{days} दिन',
        overdue: 'अतिदेय',

        // Booking Form
        customerBookingInfo: 'ग्राहक और बुकिंग जानकारी',
        editingBooking: 'बुकिंग संपादित कर रहे हैं',
        newBookingTitle: 'नई बुकिंग',
        customerShipper: 'ग्राहक / शिपर',
        contactName: 'संपर्क नाम',
        telephone: 'टेलीफोन',
        email: 'ईमेल',
        salesPerson: 'विक्रय व्यक्ति',
        blMode: 'बीएल मोड',
        officeType: 'कार्यालय प्रकार',
        fileNumber: 'फ़ाइल संख्या',
        customerRef: 'ग्राहक संदर्भ #',
        carrierBookingNumber: 'वाहक बुकिंग संख्या',
        carrierBLNumber: 'वाहक बीएल संख्या',
        bookingType: 'बुकिंग प्रकार',
        fileManager: 'फ़ाइल प्रबंधक',
        coLoaderRefNo: 'सह-लोडर संदर्भ संख्या',
        incoTerm: 'इनकोटर्म',
        rateQuoteNumber: 'दर उद्धरण संख्या',
        shippingType: 'शिपिंग प्रकार',
        fcl: 'एफसीएल',
        lcl: 'एलसीएल',
        roRo: 'आरओ-आरओ',
        fullContainer: 'पूर्ण कंटेनर',
        lessContainer: 'आंशिक कंटेनर',
        rollOnRollOff: 'रोल-ऑन/रोल-ऑफ',

        // Shipment Details
        shipmentDetails: 'शिपमेंट विवरण',
        consignee: 'कंसाइनी',
        notifyParty: 'सूचित पक्ष',
        carrier: 'वाहक',
        vessel: 'पोत',
        voyage: 'यात्रा',
        portOfLoading: 'लदान बंदरगाह',
        portOfDischarge: 'उतराई बंदरगाह',
        placeOfDelivery: 'वितरण स्थान',
        etd: 'ईटीडी',
        eta: 'ईटीए',

        // Container Details
        containerDetails: 'कंटेनर विवरण',
        containerNumber: 'कंटेनर संख्या',
        containerType: 'कंटेनर प्रकार',
        sealNumber: 'सील संख्या',
        weight: 'वजन',
        volume: 'आयतन',
        pieces: 'टुकड़े',
        packageType: 'पैकेज प्रकार',

        // Cargo Details
        cargoDetails: 'कार्गो विवरण',
        cargoDescription: 'कार्गो विवरण',
        hsCode: 'एचएस कोड',
        grossWeight: 'सकल वजन',
        netWeight: 'शुद्ध वजन',
        cbm: 'सीबीएम',

        // Print Templates
        downloadPDF: 'पीडीएफ डाउनलोड करें',
        print: 'प्रिंट करें',
        loadingQuotation: 'उद्धरण लोड हो रहा है...',
        customer: 'ग्राहक',
        rqNumber: 'आरक्यू संख्या',
        quoteDate: 'उद्धरण तिथि',
        effectiveDate: 'प्रभावी तिथि',
        expiryDate: 'समाप्ति तिथि',
        quotedBy: 'उद्धरणकर्ता',
        salesPerson: 'विक्रय व्यक्ति',
        routingInstruction: 'मार्ग निर्देश',
        chargesBreakdown: 'प्रभारों का विवरण',
        remarks: 'टिप्पणियाँ',
        subtotal: 'उप-योग',
        grandTotal: 'कुल योग',
        termsAndConditions: 'नियम और शर्तें',
        preparedBy: 'तैयार किया गया',
        authorizedSignature: 'अधिकृत हस्ताक्षर',
        thankYouForBusiness: 'आपके व्यवसाय के लिए धन्यवाद!',

        // AR/AP Section
        arApInformation: 'प्राप्य/देय खाते जानकारी',
        manageRevenueAndCost: 'राजस्व और लागत प्रभार प्रबंधित करें',
        accountsReceivable: 'प्राप्य खाते',
        accountsPayable: 'देय खाते',
        revenue: 'राजस्व',
        costs: 'लागत',
        totalAR: 'कुल प्राप्य',
        totalAP: 'कुल देय',
        profit: 'लाभ',
        margin: 'मार्जिन',
        addARCharge: 'प्राप्य प्रभार जोड़ें',
        addAPCharge: 'देय प्रभार जोड़ें',
        syncToInvoice: 'चालान से सिंक करें',
        syncToBill: 'बिल से सिंक करें',
        jobCosting: 'कार्य लागत निर्धारण',
        noARCharges: 'अभी तक कोई प्राप्य प्रभार नहीं जोड़ा गया। एक बनाने के लिए "प्राप्य प्रभार जोड़ें" पर क्लिक करें।',
        noAPCharges: 'अभी तक कोई देय प्रभार नहीं जोड़ा गया। एक बनाने के लिए "देय प्रभार जोड़ें" पर क्लिक करें।',

        // Charge Table Headers
        sno: 'क्र.सं.',
        chargeDescription: 'प्रभार विवरण',
        currency: 'मुद्रा',
        roe: 'विनिमय दर',
        basis: 'आधार',
        container: 'कंटेनर',
        quantity: 'मात्रा',
        billToParty: 'बिल प्राप्तकर्ता',
        refNumber: 'संदर्भ #',
        sellRate: 'विक्रय दर',
        buyRate: 'क्रय दर',
        ppcc: 'पीपी/सीसी',
        invoiceStatus: 'चालान स्थिति',
        billStatus: 'बिल स्थिति',
        notInvoiced: 'चालान नहीं',
        invoiced: 'चालान किया गया',
        notBilled: 'बिल नहीं',
        billed: 'बिल किया गया',

        // Rate Quote
        newRateQuote: 'नया दर उद्धरण',
        quoteNumber: 'उद्धरण संख्या',
        quoteDate: 'उद्धरण तिथि',
        validUntil: 'मान्यता तक',
        origin: 'मूल स्थान',
        destination: 'गंतव्य',
        commodity: 'वस्तु',
        cargoReadyDate: 'कार्गो तैयार तिथि',
        transitTime: 'पारगमन समय',
        days: 'दिन',
        freeDays: 'मुक्त दिन',
        demurrage: 'डेमरेज',
        detention: 'डिटेंशन',
        totalCharges: 'कुल प्रभार',
        selectCarrier: 'वाहक चुनें',
        addCarrier: 'वाहक जोड़ें',
        getQuote: 'उद्धरण प्राप्त करें',
        saveQuote: 'उद्धरण सहेजें',
        sendQuote: 'उद्धरण भेजें',

        // Print
        print: 'प्रिंट',
        printBooking: 'बुकिंग प्रिंट करें',
        printInvoice: 'चालान प्रिंट करें',
        printBill: 'बिल प्रिंट करें',
        printQuote: 'उद्धरण प्रिंट करें',
        printReport: 'रिपोर्ट प्रिंट करें',
        printPreview: 'मुद्रण पूर्वावलोकन',
        pageOf: 'पृष्ठ {current} का {total}',
        printedOn: 'मुद्रित तिथि',
        preparedBy: 'तैयारकर्ता',
        termsAndConditions: 'नियम और शर्तें',
        bankDetails: 'बैंक विवरण',
        thankYou: 'आपके व्यवसाय के लिए धन्यवाद!',

        // Common Actions
        save: 'सहेजें',
        saveBooking: 'बुकिंग सहेजें',
        cancel: 'रद्द करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        add: 'जोड़ें',
        close: 'बंद करें',
        submit: 'जमा करें',
        confirm: 'पुष्टि करें',
        back: 'पीछे',
        next: 'अगला',
        import: 'आयात',
        download: 'डाउनलोड',
        upload: 'अपलोड',
        search: 'खोजें...',
        filter: 'फ़िल्टर',
        clear: 'साफ़ करें',
        reset: 'रीसेट करें',
        apply: 'लागू करें',
        view: 'देखें',
        create: 'बनाएं',
        update: 'अद्यतन',
        actions: 'क्रियाएँ',
        moreFilters: 'अधिक फ़िल्टर',

        // Status
        loading: 'लोड हो रहा है...',
        noData: 'कोई डेटा नहीं मिला',
        success: 'सफलता',
        error: 'त्रुटि',
        warning: 'चेतावनी',
        info: 'जानकारी',
        active: 'सक्रिय',
        inactive: 'निष्क्रिय',
        completed: 'पूर्ण',
        rejected: 'अस्वीकृत',
        cancelled: 'रद्द',

        // User menu
        profile: 'प्रोफ़ाइल',
        myAccount: 'मेरा खाता',
        settings: 'सेटिंग्स',
        logout: 'लॉग आउट',
        notifications: 'सूचनाएं',
        messages: 'संदेश',
        help: 'सहायता',

        // Form validation
        required: 'आवश्यक',
        requiredFields: 'आवश्यक फ़ील्ड भरे जाने चाहिए',
        invalidEmail: 'अमान्य ईमेल पता',
        invalidPhone: 'अमान्य फ़ोन नंबर',

        // Confirmation messages
        confirmDelete: 'क्या आप वाकई इस आइटम को हटाना चाहते हैं?',
        saveChanges: 'क्या आप परिवर्तन सहेजना चाहते हैं?',
        unsavedChanges: 'आपके पास असहेज किए गए परिवर्तन हैं',
        deleteSuccess: 'सफलतापूर्वक हटा दिया गया',
        saveSuccess: 'सफलतापूर्वक सहेजा गया',
        updateSuccess: 'सफलतापूर्वक अद्यतन किया गया',
        errorOccurred: 'एक त्रुटि हुई',

        // Reports submenu
        reportsMenu: 'रिपोर्ट',
        quotationReport: 'उद्धरण रिपोर्ट',
        accountingReport: 'लेखा रिपोर्ट',
        quotesBookings: 'उद्धरण और बुकिंग',

        // System Maintenance
        systemMaintenance: 'सिस्टम रखरखाव',
        location: 'स्थान',
        countryClause: 'देश खंड',
        clauses: 'खंड',
        chargesCodeMaint: 'प्रभार कोड',

        // Document types
        invoice: 'चालान',
        creditNote: 'क्रेडिट नोट',
        debitNote: 'डेबिट नोट',
        bill: 'बिल',

        // Date/Time
        date: 'तिथि',
        time: 'समय',
        from: 'से',
        to: 'तक',
        today: 'आज',
        yesterday: 'कल',
        thisWeek: 'इस सप्ताह',
        thisMonth: 'इस महीने',
        lastMonth: 'पिछला महीना',
        thisYear: 'इस वर्ष',

        // Numbers
        subtotal: 'उप-योग',
        tax: 'कर',
        taxAmount: 'कर राशि',
        total: 'कुल',
        grandTotal: 'कुल योग',
        amountDue: 'देय राशि',
        amountPaid: 'भुगतान की गई राशि',
        balanceDue: 'शेष देय',

        // Language
        language: 'भाषा',
        selectLanguage: 'भाषा चुनें',

        // Login Page
        signIn: 'साइन इन',
        welcomeBack: 'वापसी पर स्वागत है! कृपया जारी रखने के लिए अपना विवरण दर्ज करें।',
        emailAddress: 'ईमेल पता',
        enterUsername: 'अपना उपयोगकर्ता नाम दर्ज करें',
        password: 'पासवर्ड',
        enterPassword: 'अपना पासवर्ड दर्ज करें',
        rememberMe: 'मुझे याद रखें',
        forgotPassword: 'पासवर्ड भूल गए?',
        signingIn: 'साइन इन हो रहा है...',
        dontHaveAccount: 'खाता नहीं है?',
        signUp: 'साइन अप',
        loginSuccess: 'साइन इन सफल!',
        loginFailed: 'साइन इन विफल',
        aiCapabilities: 'एआई क्षमताएँ',
        aiPlatformDesc: 'हमारा टीएमएस प्लेटफ़ॉर्म उन्नत क्षमताओं के साथ पूरी तरह से एआई-सक्षम है।',
        see: 'देखें',
        visualAnalysis: 'दृश्य विश्लेषण',
        speak: 'बोलें',
        voiceOutput: 'ध्वनि आउटपुट',
        hear: 'सुनें',
        voiceInput: 'ध्वनि इनपुट',
        think: 'सोचें',
        aiAnalysis: 'एआई विश्लेषण',
        plan: 'योजना',
        smartPlanning: 'स्मार्ट योजना',
        contentGen: 'सामग्री निर्माण',
        experienceRevolution: 'डिजिटल माल अग्रेषण में क्रांति का अनुभव करें',
        centralizedAI: 'केंद्रीकृत एआई संचालित शिपिंग प्लेटफ़ॉर्म',

        // Shipment Management
        shipmentManagement: 'शिपमेंट प्रबंधन',
        trackAndOptimize: 'एआई-संचालित अंतर्दृष्टि के साथ अपने सभी शिपमेंट को ट्रैक, प्रबंधित और अनुकूलित करें',
        totalShipments: 'कुल शिपमेंट',
        onTrack: 'ट्रैक पर',
        arrivingToday: 'आज आ रहा है',
        reviewRequired: 'समीक्षा आवश्यक',
        inTransit: 'पारगमन में',
        needsAttention: 'ध्यान देने की आवश्यकता है',
        delayed: 'विलंबित',
        all: 'सभी',
        mostRecent: 'सबसे हालिया',
        showingOf: 'कुल {total} शिपमेंट में से {shown} दिखा रहा है',
        aiInsight: 'एआई अंतर्दृष्टि',
    },
    ja: {
        aiInsights: 'AIインサイト',
        rateMatrix: '料金マトリックス',
        controlTower: 'コントロールタワー',
        notificationCenter: '通知センター',
        rateQuote: '見積もり',
        booking: '予約',
        accounting: '会計',
        chartOfAccounts: '勘定科目表',
        customerInvoices: '顧客請求書',
        vendorBills: '仕入先請求書',
        payments: '支払い',
        reports: 'レポート',

        // Dashboard
        aiDrivenSummary: 'Shipez AI駆動サマリー',
        logisticsOverview: '物流概要',
        aiSummary: 'AIサマリー',
        loadingAISummary: 'AIサマリーを読み込み中...',
        activeShipments: 'アクティブな出荷',
        pendingQuotes: '保留中の見積もり',
        inTransitContainer: '輸送中のコンテナ',
        revenue: '収益',
        fromLastMonth: '先月から',
        from: 'から',
        to: 'まで',
        create: '作成',

        // Login
        signIn: 'サインイン',
        welcomeBack: 'お帰りなさい！詳細を入力してください。',
        emailAddress: 'メールアドレス',
        enterUsername: 'ユーザー名を入力',
        password: 'パスワード',
        enterPassword: 'パスワードを入力',
        rememberMe: 'ログイン状態を保持',
        forgotPassword: 'パスワードをお忘れですか？',
        signingIn: 'サインイン中...',
        dontHaveAccount: 'アカウントをお持ちでないですか？',
        signUp: '新規登録',
        aiCapabilities: 'AI機能',
        aiPlatformDesc: '当社のTMSプラットフォームはAI対応です。',
        see: '見る',
        visualAnalysis: '視覚分析',
        speak: '話す',
        voiceOutput: '音声出力',
        hear: '聞く',
        voiceInput: '音声入力',
        think: '考える',
        aiAnalysis: 'AI分析',
        plan: '計画',
        smartPlanning: 'スマートプランニング',
        contentGen: 'コンテンツ生成',
        experienceRevolution: 'デジタル貨物輸送の革命を体験',
        centralizedAI: 'AI搭載の集中型配送プラットフォーム',

        save: '保存',
        cancel: 'キャンセル',
        delete: '削除',
        edit: '編集',
        print: '印刷',
        search: '検索...',
        loading: '読み込み中...',
        profile: 'プロフィール',
        settings: '設定',
        logout: 'ログアウト',
        systemMaintenance: 'システムメンテナンス',
        language: '言語',
    },
    ru: {
        aiInsights: 'ИИ-аналитика',
        rateMatrix: 'Матрица тарифов',
        controlTower: 'Диспетчерская',
        notificationCenter: 'Центр уведомлений',
        rateQuote: 'Расценки',
        booking: 'Бронирование',
        accounting: 'Бухгалтерия',
        chartOfAccounts: 'План счетов',
        customerInvoices: 'Счета клиентов',
        vendorBills: 'Счета поставщиков',
        payments: 'Платежи',
        reports: 'Отчёты',

        // Dashboard
        aiDrivenSummary: 'ИИ-сводка Shipez',
        logisticsOverview: 'Обзор логистики',
        aiSummary: 'ИИ-сводка',
        loadingAISummary: 'Загрузка ИИ-сводки...',
        activeShipments: 'Активные отправки',
        pendingQuotes: 'Ожидающие расценки',
        inTransitContainer: 'Контейнеры в пути',
        revenue: 'Выручка',
        fromLastMonth: 'за последний месяц',
        from: 'С',
        to: 'По',
        create: 'Создать',

        // Login
        signIn: 'Вход',
        welcomeBack: 'Добро пожаловать! Введите свои данные.',
        emailAddress: 'Электронная почта',
        enterUsername: 'Введите имя пользователя',
        password: 'Пароль',
        enterPassword: 'Введите пароль',
        rememberMe: 'Запомнить меня',
        forgotPassword: 'Забыли пароль?',
        signingIn: 'Вход...',
        dontHaveAccount: 'Нет аккаунта?',
        signUp: 'Регистрация',
        aiCapabilities: 'Возможности ИИ',
        aiPlatformDesc: 'Наша TMS-платформа полностью поддерживает ИИ.',
        see: 'Видеть',
        visualAnalysis: 'Визуальный анализ',
        speak: 'Говорить',
        voiceOutput: 'Голосовой вывод',
        hear: 'Слышать',
        voiceInput: 'Голосовой ввод',
        think: 'Думать',
        aiAnalysis: 'ИИ-анализ',
        plan: 'Планировать',
        smartPlanning: 'Умное планирование',
        contentGen: 'Генерация контента',
        experienceRevolution: 'Испытайте революцию в цифровых грузоперевозках',
        centralizedAI: 'Централизованная платформа доставки на базе ИИ',

        save: 'Сохранить',
        cancel: 'Отмена',
        delete: 'Удалить',
        edit: 'Редактировать',
        print: 'Печать',
        search: 'Поиск...',
        loading: 'Загрузка...',
        profile: 'Профиль',
        settings: 'Настройки',
        logout: 'Выйти',
        systemMaintenance: 'Обслуживание системы',
        language: 'Язык',
    },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('app_language');
        return saved || 'en';
    });

    useEffect(() => {
        localStorage.setItem('app_language', language);
        const selectedLang = languages.find(l => l.code === language);
        document.documentElement.dir = selectedLang?.rtl ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
    }, [language]);

    const t = (key, params = {}) => {
        let text = translations[language]?.[key] || translations['en']?.[key] || key;
        Object.keys(params).forEach(param => {
            text = text.replace(`{${param}}`, params[param]);
        });
        return text;
    };

    const currentLanguage = languages.find(l => l.code === language) || languages[0];

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage,
            t,
            languages,
            currentLanguage,
            isRTL: currentLanguage?.rtl || false
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext;
